import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Factory, KeyRound, ArrowLeft, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const tokenFromUrl = sp.get("token") || "";
  const [token, setToken] = useState(tokenFromUrl);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) { toast.error("Senhas não conferem"); return; }
    if (pwd.length < 8) { toast.error("Senha deve ter ao menos 8 caracteres"); return; }
    if (!token) { toast.error("Token obrigatório"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success("Senha redefinida com sucesso!");
      setTimeout(() => nav("/login"), 2000);
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Falha ao redefinir senha");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B140F] text-white flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#234935]/40 blur-[120px]" />
        <div className="absolute top-1/3 -right-48 h-[480px] w-[480px] rounded-full bg-[#C5A059]/20 blur-[140px]" />
      </div>
      <div className="relative z-10 w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-[12px] text-white/60 hover:text-white mb-6" data-testid="rp-back-login">
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar ao login
        </Link>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="grid h-11 w-11 place-items-center rounded-lg bg-gradient-to-br from-[#234935] to-[#1A231F] ring-1 ring-[#C5A059]/30">
              <Factory className="h-5 w-5 text-[#C5A059]" />
            </div>
            <div>
              <div className="font-display text-[17px] font-semibold">Definir nova senha</div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/60">TheoKratos RH</div>
            </div>
          </div>

          {done ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 rounded-md border border-emerald-400/30 bg-emerald-400/10 p-4">
                <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                <div className="text-[13px]">Senha atualizada! Redirecionando…</div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {!tokenFromUrl && (
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-white/70">Token de recuperação</Label>
                  <div className="relative mt-1">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <Input value={token} onChange={(e) => setToken(e.target.value)} required
                           placeholder="Cole aqui o token recebido"
                           className="pl-10 bg-white/[0.04] border-white/10 text-white placeholder:text-white/30"
                           data-testid="rp-token-input" />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-white/70">Nova senha (mín. 8 caracteres)</Label>
                <div className="relative mt-1">
                  <Input type={show ? "text" : "password"} value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={8}
                         className="pr-10 bg-white/[0.04] border-white/10 text-white placeholder:text-white/30"
                         data-testid="rp-pwd-input" />
                  <button type="button" onClick={() => setShow(!show)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-white/70">Confirme a nova senha</Label>
                <Input type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                       className="bg-white/[0.04] border-white/10 text-white mt-1" data-testid="rp-confirm-input" />
              </div>
              <Button type="submit" disabled={loading || !pwd || !confirm}
                      className="w-full bg-[#C5A059] text-[#1A231F] hover:bg-[#D4B36B] font-semibold"
                      data-testid="rp-submit-btn">
                {loading ? "Redefinindo..." : "Redefinir senha"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
